/**
 * @fileoverview The third source of a page's key: a collection you hold a key
 * for, whose sealed member list names that page.
 *
 * ---
 *
 * WHY THIS IS A SOURCE AND NOT A NAVIGATION DETAIL, which is the whole ruling.
 *
 * The obvious way to open a private member is to tell the page loader which
 * collection the reader came through, on the route or in navigation state. It
 * works on the first click and is wrong twice over.
 *
 * It makes a page's openability depend on HOW IT WAS REACHED. A bookmark, a
 * link the recipient was sent directly, or a refresh all arrive without that
 * context and read as unavailable. The reader experiences it as intermittent,
 * which is the hardest class of bug to report and the easiest to dismiss.
 *
 * And it puts the collection's address beside the page's address in a URL. A
 * private member's address never appears in a tag because the list of addresses
 * IS the shape of the private site; a share record was rewritten for naming its
 * document in the clear. A URL is the one place that gets copied, pasted, logged
 * by an intermediary and sent as a referrer, so putting both addresses there
 * reintroduces exactly that leak one layer up.
 *
 * So resolution asks a question about what this READER HOLDS, never about where
 * they have been. Ruled by the operator via `cloistr-orchestrator`, 2026-09-17.
 *
 * ---
 *
 * EVERY CANDIDATE IS TRIED, AND THE LOOP ENDS ON THE FIRST SUCCESSFUL OPEN
 * RATHER THAN THE FIRST COLLECTION. A page can belong to two collections this
 * reader holds, and the first one checked may not carry a usable key row for it:
 * the author may have added the page before the key layer existed, or removed
 * and re-added it, or rotated that member's key and rewritten only one of the
 * two. A loop that returned after looking in the first collection would pass
 * every single-collection test and fail that one, silently, as unavailable.
 *
 * ---
 *
 * A CACHE WOULD BE A FINE OPTIMISATION AND A DISASTROUS MECHANISM. If the keys
 * were populated as a side effect of rendering the collection, then opening a
 * member by its own address in a fresh session would fail while the demo of
 * clicking through from the site would pass. Nothing here remembers anything
 * between calls; a cache may be added in front of it, never in place of it.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth/core';

import { parseCollectionEvent } from './collectionEvent';
import { readOwnerKey } from './collectionKeys';
import { PAGE_COLLECTION_KIND, PAGE_INDEX_KIND } from './kinds';
import { isPrivatePageEvent } from './privatePage';
import { type Share, sharesFor } from './shareCollection';

import { fetchAll, fetchNewest } from '@/services/nostr/relays';

/** `kind:pubkey:d`, or null for anything that is not one. */
function parseCoordinate(address: string): { kind: number; pubkey: string; identifier: string } | null {
  const parts = address.split(':');
  if (parts.length !== 3) return null;
  const [kind, pubkey, identifier] = parts;
  if (!kind || !pubkey || !identifier) return null;
  if (!/^\d+$/.test(kind) || !/^[0-9a-f]{64}$/i.test(pubkey)) return null;
  return { kind: Number(kind), pubkey, identifier };
}

/**
 * The key for one page, from any collection this reader was given.
 *
 * `shares` is passed in when the caller already has them, which the page loader
 * does: the share lookup for the page itself and this one read the same list,
 * and fetching it twice would double the cost of the uncommon path for nothing.
 *
 * NOTHING HERE THROWS. Every failure means this reader cannot open this page,
 * which is a real answer and the same one a stranger gets.
 */
export async function memberKeyFromCollections(
  pageAddress: string,
  signer: SignerInterface,
  relays: readonly string[],
  shares?: readonly Share[]
): Promise<ContentKey | null> {
  let held: readonly Share[];
  try {
    held = shares ?? (await sharesFor(signer, relays)).shares;
  } catch {
    return null;
  }

  const collections = held
    .map((share) => ({ share, coordinate: parseCoordinate(share.address) }))
    .filter(
      (c): c is { share: Share; coordinate: NonNullable<ReturnType<typeof parseCoordinate>> } =>
        c.coordinate !== null && c.coordinate.kind === PAGE_COLLECTION_KIND
    );
  if (collections.length === 0) return null;

  // ONE QUERY FOR ALL OF THEM. A reader in ten shared sites should not open ten
  // subscriptions to answer one question about one page. The filter is a cross
  // product of authors and identifiers, so it can return collections we did not
  // ask for; each answer is matched back to an exact coordinate below.
  let events;
  try {
    events = await fetchAll([...relays], {
      kinds: [PAGE_COLLECTION_KIND],
      authors: [...new Set(collections.map((c) => c.coordinate.pubkey))],
      '#d': [...new Set(collections.map((c) => c.coordinate.identifier))],
    });
  } catch {
    return null;
  }

  for (const { share, coordinate } of collections) {
    // THE ADDRESS ASKED FOR BEATS THE ANSWER THAT CAME BACK, the same rule the
    // page and theme loaders follow. The relays here include hints chosen by
    // whoever produced a link, and a signature proves only that somebody signed.
    const newest = events
      .filter(
        (e) =>
          e.pubkey === coordinate.pubkey &&
          e.tags.some((t) => t[0] === 'd' && t[1] === coordinate.identifier)
      )
      .reduce<(typeof events)[number] | null>(
        (best, e) => (!best || e.created_at > best.created_at ? e : best),
        null
      );
    if (!newest) continue;

    let collection;
    try {
      collection = parseCollectionEvent(newest, share.key);
    } catch {
      continue;
    }
    const key = collection?.memberKeys.get(pageAddress);
    // Keep going on a miss. See the file comment: the first collection checked
    // is not necessarily the one carrying a usable row for this page.
    if (key) return key;
  }

  return null;
}

/**
 * The WRITE-SIDE COUNTERPART: a page's key, recovered by its own author, so the
 * collection they are building can carry it.
 *
 * IN THIS FILE BECAUSE IT IS THE SAME KEY AT THE OTHER END OF THE SAME WIRE.
 * Split across two modules, the pair reads as two unrelated lookups and the
 * reason they have to agree about the subject binding is nowhere.
 *
 * PAID ONCE, WHEN A MEMBER IS ADDED, and that is the whole economic argument.
 * Recovering a key means a relay round trip and a signer prompt, which on a
 * remote signer is a notification on somebody's phone. Doing it at publish time
 * would charge a forty-page site forty prompts on every save; doing it at add
 * time charges one, for the page the author is looking at, and every later edit
 * reuses the keys already inside the sealed half.
 *
 * FOUR ANSWERS, NOT TWO, because the caller has something different to say for
 * each. A public page needs no key and is not a problem. A page this author
 * cannot open is one they must not add, because a member nobody can open is the
 * false success this whole change exists to remove.
 */
export type OwnedPageKey =
  | { status: 'public' }
  | { status: 'key'; key: ContentKey }
  | { status: 'not-found' }
  | { status: 'not-yours' };

export async function pageKeyForOwner(
  address: string,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<OwnedPageKey> {
  const coordinate = parseCoordinate(address);
  if (!coordinate) return { status: 'not-found' };
  if (coordinate.kind !== PAGE_INDEX_KIND) {
    // Only our own kind can be private. A long-form article is plaintext by
    // definition, so there is no key to look for and nothing to refuse.
    return { status: 'public' };
  }

  let event;
  try {
    event = await fetchNewest([...relays], {
      kinds: [PAGE_INDEX_KIND],
      authors: [coordinate.pubkey],
      '#d': [coordinate.identifier],
    });
  } catch {
    return { status: 'not-found' };
  }
  // The address asked for beats the answer that came back, here as everywhere.
  if (
    !event ||
    event.pubkey !== coordinate.pubkey ||
    !event.tags.some((t) => t[0] === 'd' && t[1] === coordinate.identifier)
  ) {
    return { status: 'not-found' };
  }

  if (!isPrivatePageEvent(event)) return { status: 'public' };

  try {
    const key = await readOwnerKey(event.tags, address, signer);
    return key ? { status: 'key', key } : { status: 'not-yours' };
  } catch {
    // A key tag that is present and will not open means this signer is not the
    // one it was wrapped to, which is the ordinary case for somebody else's
    // private page rather than an error.
    return { status: 'not-yours' };
  }
}
